import React, { useState, useEffect } from 'react';
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifButton,
  MotifErrorMessage,
  MotifToast,
} from '@ey-xd/motif-react';
import PropTypes from 'prop-types';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import * as formHelpers from '../../scripts/formHelpers';
import * as formValidators from '../../scripts/formValidators';
import {
  DateField,
  MoneyField,
  NumberField,
  PhoneField,
} from '../shared/formFields';
import * as states from '../../constants/states';
import * as parsers from '../../scripts/formParsers';
import { useSelector } from 'react-redux';

const AddEditPropertyModal = ({
  show,
  onClose,
  reits = [],
  lookupTypes,
  handleSubmit,
  onSave,
  initialValues = {},
  isEdit = false,
  validationMessage,
  clearValidationMessage,
  errorMessage,
  handleDismissErrorMessage,
  handleShowEndForm,
  handleShowClearEndForm,
  acquisitionValidationMessage,
}) => {
  const [selectedREIT, setSelectedREIT] = useState(initialValues?.reitID || '');
  const [showPsqTypes, setShowPsqTypes] = useState(true);
  const modalHeader = isEdit ? 'Edit Property' : 'Add New Property';
  const [acquisitionDateValidationError, setAcquisitionDateValidationError] =
    useState('');
  const selector = formValueSelector('addEditPropertyMasterForm');
  const acquisitionDate = useSelector((state) =>
    selector(state, 'acquisitionDate')
  );
  const endDate = useSelector((state) => selector(state, 'endDate'));

  useEffect(() => {
    if (initialValues && initialValues?.reitID) {
      setSelectedREIT(initialValues.reitID);
    }
  }, [initialValues]);

  const rerenderPsqTypes = function (e) {
    setShowPsqTypes(false);

    setTimeout(() => {
      setShowPsqTypes(true);
    });
  };

  useEffect(() => {
    if (acquisitionDate && endDate) {
      const acquisitionDateObj = new Date(acquisitionDate);
      const endDateObj = new Date(endDate);

      // Compare acquisition date and end date
      if (acquisitionDateObj > endDateObj) {
        setAcquisitionDateValidationError(
          'Acquisition date cannot be later than the End Date.'
        );
      } else {
        setAcquisitionDateValidationError('');
      }
    } else {
      setAcquisitionDateValidationError('');
    }
  }, [acquisitionDate]);

  const submitForm = (formValues) => {
    if (acquisitionDateValidationError === '') {
      onSave(formValues);
    }
  };

  const handleEndPropertyClick = () => {
    onClose(); // Close the current modal first
    handleShowEndForm(initialValues?.propertyMasterID); // Then open the end property modal
  };

  const handleClearEndPropertyClick = () => {
    onClose(); // Close the current modal first
    handleShowClearEndForm(initialValues?.propertyMasterID); // Then open the end property modal
  };

  return (
    <MotifModal
      variant="alt"
      size="xl"
      show={show}
      onClose={onClose}
      data-testid={
        isEdit ? 'divEditNewPropertyModal' : 'divAddNewPropertyModal'
      }
    >
      <MotifModalHeader
        data-testid={isEdit ? 'mdHdrEditNewProperty' : 'mdHdrAddNewProperty'}
      >
        <span className="ml-2">{modalHeader}</span>
      </MotifModalHeader>
      <MotifModalBody>
        <form
          onSubmit={handleSubmit(submitForm)}
          className="addEditPropertyMasterForm"
        >
          {formHelpers.showSubmissionError(
            errorMessage,
            handleDismissErrorMessage
          )}
          <Field
            name="propertyMasterID"
            component={formHelpers.renderField}
            type="hidden"
          />

          <section className="reit">
            <div className="row">
              <div className="field col-md-6" style={{ minHeight: '44px' }}>
                <Field
                  name="reitid"
                  label="REIT Name (Required)"
                  placeholder="Select a REIT Name"
                  component={formHelpers.SingleSelectComponent}
                  type="select"
                  options={reits}
                  optionValue={(o) => o.reitid}
                  optionText={(o) => `${o.reitShortName}_${o.reitTrackingID}`}
                  validate={[formValidators.requiredSelect]}
                  testingData={{ label: 'lblREITName', div: 'divREITName' }}
                  disabled={isEdit}
                />
              </div>
            </div>

            <div className="row">
              <div className="field col-md-3">
                <Field
                  name="propertyTrackingID"
                  component={formHelpers.renderField}
                  type="text"
                  label="REITSuite Entity ID"
                  dataTestId="txtREITSuiteEntityID"
                  disabled={true}
                  value={initialValues?.propertyTrackingID || ''}
                />
              </div>

              <div className="field col-md-3">
                <Field
                  name="gtpEntityID"
                  component={formHelpers.renderField}
                  type="text"
                  maxLength={50}
                  label="GTP Entity ID"
                  dataTestId="txtGTPEntityID"
                />
              </div>

              <div className="field col-md-3">
                <Field
                  name="clientEntityID"
                  component={formHelpers.renderField}
                  type="text"
                  maxLength={50}
                  label="Client Entity ID"
                  dataTestId="txtClientEntityID"
                />
              </div>
            </div>

            <div className="row">
              <div className="field col-md-6">
                <Field
                  name="propertyName"
                  component={formHelpers.renderField}
                  type="text"
                  maxLength={255}
                  label="Property Name"
                  dataTestId="txtPropertyName"
                  isRequiredField={true}
                  validate={[
                    formValidators.required,
                    formValidators.minLength2,
                    formValidators.maxLength255,
                    formValidators.noWindowSpecialChars,
                  ]}
                  onChange={clearValidationMessage}
                />
                {(validationMessage && (
                  <MotifErrorMessage variant="error">
                    {validationMessage}
                  </MotifErrorMessage>
                )) ||
                  null}
              </div>

              <div className="field col-md-3">
                <Field
                  name="propertyTypeID"
                  label="Property Type"
                  component={formHelpers.SingleSelectComponent}
                  type="select"
                  placeholder="Select one"
                  options={lookupTypes.propertyTypes?.filter(
                    (pt) => pt.propertyTypeID !== 0
                  )}
                  optionValue={(opt) => opt.propertyTypeID}
                  optionText={(opt) => opt.propertyTypeDescription}
                  onChange={(e) => rerenderPsqTypes(e)}
                  testingData={{
                    label: 'lblPropertyType',
                    div: 'divPropertyType',
                  }}
                />
              </div>

              <div className="field col-md-3">
                {showPsqTypes && (
                  <Field
                    name="psqPropertyTypes"
                    label="PSQ Property Type(s)"
                    placeholder="Select one or more"
                    options={lookupTypes.psqPropertyTypes?.filter(
                      (ppt) => ppt.psqPropertyTypeID !== 0
                    )}
                    optionText={(o) => o.psqPropertyTypeDescription}
                    optionValue={(o) =>
                      o.psqPropertyTypeDescription?.replace(
                        'Multi-Family',
                        'MultiFamily'
                      )
                    }
                    component={formHelpers.MultipleSelect}
                    testingData={{
                      label: 'lblPSQPropertyType',
                      div: 'divPSQPropertyType',
                    }}
                  />
                )}
              </div>
            </div>

            <div className="row">
              {showPsqTypes && isEdit && (
                <div className="field col-md-12">
                  <MotifToast
                    variant="error"
                    className="hide-close-button"
                    data-testid="psqPropertyTypeToastMsg"
                  >
                    Warning: Removing the PSQ Property Type(s) will remove this
                    property from the checklist auto delivery calendar. Please
                    note that this field needs to be populated to utilize the
                    checklist auto delivery calendar for this property.
                  </MotifToast>
                </div>
              )}
            </div>

            <div className="row">
              <div className="field col-md-3">
                <Field
                  name="street"
                  component={formHelpers.renderField}
                  type="text"
                  label="Street Address"
                  maxLength={255}
                  isRequiredField={true}
                  validate={[
                    formValidators.required,
                    formValidators.minLength2,
                    formValidators.maxLength255,
                  ]}
                  dataTestId="txtStreetAddress"
                />
              </div>

              <div className="field col-md-3">
                <Field
                  name="city"
                  component={formHelpers.renderField}
                  type="text"
                  label="City"
                  maxLength={255}
                  isRequiredField={true}
                  validate={[
                    formValidators.required,
                    formValidators.minLength2,
                    formValidators.maxLength255,
                  ]}
                  dataTestId="txtCity"
                />
              </div>

              <div className="field col-md-3">
                <Field
                  name="state"
                  label="State (Required)"
                  component={formHelpers.SingleSelectComponent}
                  type="select"
                  placeholder="Select one"
                  isRequiredField={true}
                  validate={[
                    formValidators.required,
                    formValidators.minLength2,
                  ]}
                  options={states.usStates}
                  optionValue={(s) => s.value}
                  optionText={(s) => s.label}
                  testingData={{ label: 'lblState', div: 'divState' }}
                />
              </div>

              <div className="field col-md-3">
                <Field
                  name="zipCode"
                  label="ZIP Code"
                  type="text"
                  maxLength={10}
                  isRequiredField={true}
                  component={formHelpers.renderField}
                  validate={[formValidators.required, formValidators.zipCode]}
                  dataTestId="txtZipCode"
                />
              </div>
            </div>

            <div className="row">
              <div className="field col-md-3">
                <NumberField
                  name="rentableSquareFootage"
                  allowDecimal={false}
                  allowNegativeValues={false}
                  label="Rentable Sq Ft"
                  placeholder="0"
                  validate={[formValidators.number, formValidators.maxValueInt]}
                  parse={parsers.positiveWholeNumberParser}
                  dataTestId="txtRentable"
                />
              </div>

              <div className="field col-md-3">
                <MoneyField
                  name="purchasePrice"
                  label="Purchase Price"
                  placeholder="0.00"
                  validate={[
                    formValidators.number,
                    formValidators.maxValueDecimal,
                  ]}
                  dataTestId="txtPurchasePrice"
                />
              </div>

              <div className="field col-md-3">
                <DateField
                  name="acquisitionDate"
                  label="Acquisition Date"
                  placeholder="MM/DD/YYYY"
                  isRequiredField={true}
                  validate={[
                    formValidators.required,
                    formValidators.isValidDate,
                  ]}
                  dataTestId="txtAcquisitionDate"
                  onChange={clearValidationMessage}
                />
                {acquisitionValidationMessage ? (
                  <MotifErrorMessage variant="error">
                    {acquisitionValidationMessage}
                  </MotifErrorMessage>
                ) : acquisitionDateValidationError ? (
                  <MotifErrorMessage variant="error">
                    {acquisitionDateValidationError}
                  </MotifErrorMessage>
                ) : null}
              </div>

              <div className="field col-md-3">
                <DateField
                  name="endDate"
                  label="End Date"
                  validate={[formValidators.isValidDate]}
                  disabled
                  dataTestId="txtEndDate"
                />
              </div>
            </div>

            <div className="row">
              <div className="field col-md-3">
                <Field
                  name="propertyClassID"
                  label="Class"
                  component={formHelpers.SingleSelectComponent}
                  type="select"
                  placeholder="Select one"
                  options={lookupTypes.propertyClasses?.filter(
                    (pc) => pc.propertyClassID !== 0
                  )}
                  optionValue={(opt) => opt.propertyClassID}
                  optionText={(opt) => opt.propertyClassDescription}
                  testingData={{ label: 'lblClass', div: 'divClass' }}
                />
              </div>

              <div className="field col-md-3">
                <Field
                  name="tenantTypeID"
                  label="Tenant Type"
                  component={formHelpers.SingleSelectComponent}
                  type="select"
                  placeholder="Select one"
                  options={lookupTypes.tenantTypes?.filter(
                    (tt) => tt.tenantTypeID != 0
                  )}
                  optionValue={(opt) => opt.tenantTypeID}
                  optionText={(opt) => opt.tenantTypeDescription}
                  testingData={{ label: 'lblTenantType', div: 'divTenantType' }}
                />
              </div>

              <div className="field col-md-3">
                <Field
                  name="propertyParkingTypeID"
                  label="Parking"
                  component={formHelpers.SingleSelectComponent}
                  type="select"
                  placeholder="Select one"
                  options={lookupTypes.propertyParkingTypes?.filter(
                    (ppt) => ppt.propertyParkingTypeID != 0
                  )}
                  optionValue={(opt) => opt.propertyParkingTypeID}
                  optionText={(opt) => opt.propertyParkingTypeDescription}
                  testingData={{ label: 'lblParking', div: 'divParking' }}
                />
              </div>
            </div>

            <hr className="margin-top-5" />

            <div className="row">
              <div className="field col-md-6">
                <Field
                  name="managementCompany"
                  component={formHelpers.renderField}
                  type="text"
                  maxLength={255}
                  validate={[formValidators.maxLength255]}
                  label="Management Company"
                  dataTestId="txtManagementCompany"
                />
              </div>
            </div>

            <div className="row">
              <div className="field col-md-3">
                <Field
                  name="managerName"
                  component={formHelpers.renderField}
                  type="text"
                  label="Manager Name"
                  maxLength={255}
                  validate={[
                    formValidators.noDoubleQuotes,
                    formValidators.maxLength255,
                  ]}
                  dataTestId="txtManagerName"
                />
              </div>

              <div className="field col-md-6">
                <Field
                  name="managerEmail"
                  component={formHelpers.renderField}
                  type="email"
                  label="Manager Email"
                  maxLength={255}
                  validate={[formValidators.email, formValidators.maxLength255]}
                  dataTestId="txtManagerEmail"
                />
              </div>

              <div className="field col-md-3">
                <PhoneField
                  name="managerPhone"
                  label="Manager Phone"
                  placeholder="(###) ###-####"
                  validate={[formValidators.phoneNumber]}
                  dataTestId="txtManagerPhone"
                />
              </div>
            </div>

            <div className="row">
              <div className="field col-md-3">
                <Field
                  name="accountantName"
                  maxLength={255}
                  validate={[formValidators.maxLength255]}
                  component={formHelpers.renderField}
                  type="text"
                  label="Accountant Name"
                  dataTestId="txtAccountantName"
                />
              </div>

              <div className="field col-md-6">
                <Field
                  name="accountantEmail"
                  component={formHelpers.renderField}
                  type="email"
                  label="Accountant Email"
                  maxLength={255}
                  validate={[formValidators.email, formValidators.maxLength255]}
                  dataTestId="txtAccountantEmail"
                />
              </div>

              <div className="field col-md-3">
                <PhoneField
                  name="accountantPhone"
                  label="Accountant Phone"
                  placeholder="(###) ###-####"
                  validate={[formValidators.phoneNumber]}
                  dataTestId="txtAccountantPhone"
                />
              </div>
            </div>
          </section>
        </form>
      </MotifModalBody>
      <MotifModalFooter>
        <MotifButton
          variant="secondary"
          type="button"
          onClick={onClose}
          className="right mt-3"
          data-testid="btnCancel"
        >
          Cancel
        </MotifButton>
        {isEdit &&
          (initialValues?.endDate ? (
            <MotifButton
              variant="secondary"
              type="button"
              className="right mt-3"
              data-testid="btnClearEndDate"
              onClick={handleClearEndPropertyClick}
            >
              Clear End Date
            </MotifButton>
          ) : (
            <MotifButton
              variant="secondary"
              type="button"
              className="right mt-3"
              data-testid="btnEndProperty"
              onClick={handleEndPropertyClick}
            >
              End Property
            </MotifButton>
          ))}
        <MotifButton
          type="submit"
          className="right mt-3"
          data-testid="btnSubmit"
          onClick={handleSubmit(submitForm)}
        >
          Submit
        </MotifButton>
      </MotifModalFooter>
    </MotifModal>
  );
};

AddEditPropertyModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  modalHeader: PropTypes.string,
  reits: PropTypes.array,
  lookupTypes: PropTypes.array,
  handleSubmit: PropTypes.func,
  onSave: PropTypes.func,
  initialValues: PropTypes.object,
  isEdit: PropTypes.bool,
  validationMessage: PropTypes.string,
  clearValidationMessage: PropTypes.func,
  errorMessage: PropTypes.string,
  handleDismissErrorMessage: PropTypes.func,
  handleShowEndForm: PropTypes.func,
  handleShowClearEndForm: PropTypes.func,
  acquisitionValidationMessage: PropTypes.string,
};

export default reduxForm({
  form: 'addEditPropertyMasterForm',
  enableReinitialize: true,
})(AddEditPropertyModal);
